import { DecoderChunker, EncoderChunker, } from "./chunker.js";
import { Decoder } from "./decoder.js";
import { Encoder, } from "./encoder.js";
import { LzInWindow } from "./lz-in-window.js";
import { computeHashSize, computeWindowReservSize, ensureCyclicBuffer, isDictionarySizeBelowThreshold, setCutValue, setMatchMaxLen, } from "./match-finder-config.js";
import { _MAX_UINT32, add64, arraycopy, compare64, CRC32_TABLE, createBitTree, DICTIONARY_SIZE_THRESHOLD, fromInt64, G_FAST_POS, getBitPrice, getLenToPosState, INFINITY_PRICE, initArray, lowBits64, N1_LONG_LIT, P0_LONG_LIT, P1_LONG_LIT, PROB_PRICES, shr64, stateUpdateChar, sub64, } from "./utils.js";
/**
 * Compression modes
 */
export const MODES = {
    1: { searchDepth: 0x10, filterStrength: 0x40, modeIndex: 0x00 },
    2: { searchDepth: 0x14, filterStrength: 0x40, modeIndex: 0x00 },
    3: { searchDepth: 0x13, filterStrength: 0x40, modeIndex: 0x01 },
    4: { searchDepth: 0x14, filterStrength: 0x40, modeIndex: 0x01 },
    5: { searchDepth: 0x15, filterStrength: 0x80, modeIndex: 0x01 },
    6: { searchDepth: 0x16, filterStrength: 0x80, modeIndex: 0x01 },
    7: { searchDepth: 0x17, filterStrength: 0x80, modeIndex: 0x01 },
    8: { searchDepth: 0x18, filterStrength: 0xFF, modeIndex: 0x01 },
    9: { searchDepth: 0x19, filterStrength: 0xFF, modeIndex: 0x01 },
};
export class LZMA {
    _encoder;
    _decoder;
    _lzInWindow = null;
    _compressor;
    _decompressor;
    constructor() {
        this._encoder = new Encoder();
        this._decoder = new Decoder();
        this._compressor = this._initCompressor();
        this._decompressor = this._initDecompressor();
    }
    _initCompressor() {
        const encoderChunker = new EncoderChunker(this);
        return {
            chunker: encoderChunker,
            output: {
                buf: initArray(32),
                count: 0,
                write: () => { },
            },
        };
    }
    _initDecompressor() {
        const decoderChunker = new DecoderChunker(this._decoder);
        return {
            chunker: decoderChunker,
            output: {
                buf: initArray(0x20),
                count: 0,
                write: () => { },
            },
        };
    }
    _read(inputStream) {
        if (inputStream.pos >= inputStream.count) {
            return -1;
        }
        let value;
        if (inputStream.buf instanceof ArrayBuffer) {
            value = new Uint8Array(inputStream.buf)[inputStream.pos++];
        }
        else if (inputStream.buf instanceof Uint8Array) {
            value = inputStream.buf[inputStream.pos++];
        }
        else {
            value = inputStream.buf[inputStream.pos++];
        }
        return value & 0xFF;
    }
    _toByteArray(output) {
        const data = output.buf.slice(0, output.count);
        return data;
    }
    _write(buffer, b) {
        if (!buffer)
            return;
        if (buffer.count >= buffer.buf.length) {
            const newSize = Math.max(buffer.buf.length * 2, buffer.count + 1);
            const newBuf = new Array(newSize);
            for (let i = 0; i < buffer.count; i++) {
                newBuf[i] = buffer.buf[i];
            }
            buffer.buf = newBuf;
        }
        buffer.buf[buffer.count++] = b << 24 >> 24;
    }
    _write_0(buffer, buf, off, len) {
        const requiredSize = buffer.count + len;
        if (requiredSize > buffer.buf.length) {
            const newSize = Math.max(buffer.buf.length * 2, requiredSize);
            const newBuf = new Array(newSize);
            for (let i = 0; i < buffer.count; i++) {
                newBuf[i] = buffer.buf[i];
            }
            buffer.buf = newBuf;
        }
        arraycopy(buf, off, buffer.buf, buffer.count, len);
        buffer.count += len;
    }
    _getChars(inputString, srcBegin, srcEnd, dst, dstBegin) {
        for (let srcIdx = srcBegin; srcIdx < srcEnd; ++srcIdx) {
            dst[dstBegin++] = inputString.charCodeAt(srcIdx);
        }
    }
    _configure(mode) {
        this._encoder.initialize();
        this._encoder.configure(mode);
    }
    _initCompression(input, len, mode) {
        if (compare64(len, N1_LONG_LIT) < 0) {
            throw new Error("invalid length " + len);
        }
        this._compressor.length_0 = len;
        this._Encoder();
        this._configure(mode);
        this.writeHeaderProperties();
        for (let i = 0; i < 64; i += 8) {
            this._write(this._compressor.output, lowBits64(shr64(len, i)) & 0xFF);
        }
        this._encoder._needReleaseMFStream = 0;
        this._encoder._inStream = input;
        this._encoder._finished = 0;
        this._Create_2();
        this._encoder._rangeEncoder.stream = this._compressor.output;
        this._encoder.init();
        this._encoder.fillDistancesPrices();
        this._encoder.fillAlignPrices();
        this._encoder._lenEncoder.setTableSize(this._encoder._numFastBytes + 1 - 2);
        this._encoder._lenEncoder.updateTables(1 << this._encoder._posStateBits);
        this._encoder._repMatchLenEncoder.setTableSize(this._encoder._numFastBytes + 1 - 2);
        this._encoder._repMatchLenEncoder.updateTables(1 << this._encoder._posStateBits);
        this._encoder.nowPos64 = P0_LONG_LIT;
        this._compressor.chunker.encoder = this._encoder;
        this._compressor.chunker.alive = 1;
    }
    _byteArrayCompressor(data, mode) {
        const inputSize = data instanceof ArrayBuffer ? data.byteLength : data.length;
        const estimatedOutputSize = Math.max(32, Math.ceil(inputSize * 1.2));
        this._compressor.output = {
            buf: initArray(estimatedOutputSize),
            count: 0,
            write: () => { },
        };
        const inputBuffer = {
            pos: 0,
            buf: data instanceof ArrayBuffer
                ? new Uint8Array(data)
                : data,
            count: data instanceof ArrayBuffer
                ? new Uint8Array(data).length
                : data.length,
        };
        this._initCompression(inputBuffer, fromInt64(data instanceof ArrayBuffer ? data.byteLength : data.length), mode);
    }
    _initDecompression(input) {
        let hex_length = "", properties = [], r, tmp_length;
        for (let i = 0; i < 5; ++i) {
            r = this._read(input);
            if (r == -1) {
                throw new Error("truncated input");
            }
            properties[i] = r << 24 >> 24;
        }
        const isDecoderInitialized = !this._decoder.setDecoderProperties(properties)
            ? 1
            : 0;
        if (isDecoderInitialized) {
            throw new Error("corrupted input");
        }
        for (let i = 0; i < 64; i += 8) {
            r = this._read(input);
            if (r == -1) {
                throw new Error("truncated input");
            }
            r = r.toString(0x10);
            if (r.length == 1)
                r = "0" + r;
            hex_length = r + "" + hex_length;
        }
        /**
         * Was the length set in the header (if it was compressed from a stream, the
         * length is all f"s).
         */
        if (/^0+$|^f+$/i.test(hex_length)) {
            this._compressor.length_0 = N1_LONG_LIT;
        }
        else {
            /**
             * NOTE: If there is a problem with the decoder because of the length,
             * you can always set the length to -1 (N1_longLit) which means unknown.
             */
            tmp_length = parseInt(hex_length, 0x10);
            if (tmp_length > _MAX_UINT32) {
                this._compressor.length_0 = N1_LONG_LIT;
            }
            else {
                this._compressor.length_0 = fromInt64(tmp_length);
            }
        }
        this._decompressor.chunker = this._CodeInChunks(input, this._compressor.length_0);
    }
    _byteArrayDecompressor(data) {
        const inputDataSize = data instanceof ArrayBuffer ? data.byteLength : data.length;
        const minBufferSize = 0x20; // 32 bytes minimum
        const estimatedOutputSize = inputDataSize * 2; // Estimate 2x expansion for decompression
        const initialBufferSize = Math.max(minBufferSize, estimatedOutputSize);
        this._decompressor.output = {
            buf: initArray(initialBufferSize),
            count: 0,
            write: () => { },
        };
        const inputBuffer = {
            buf: data,
            pos: 0,
            count: data instanceof ArrayBuffer ? data.byteLength : data.length,
        };
        this._initDecompression(inputBuffer);
    }
    _Create_4(keepSizeBefore, keepSizeAfter, keepSizeReserv) {
        let blockSize;
        this._encoder._matchFinder._keepSizeBefore = keepSizeBefore;
        this._encoder._matchFinder._keepSizeAfter = keepSizeAfter;
        blockSize = keepSizeBefore + keepSizeAfter + keepSizeReserv;
        if (this._encoder._matchFinder._bufferBase == null || this._encoder._matchFinder._blockSize != blockSize) {
            this._encoder._matchFinder._bufferBase = initArray(blockSize);
            this._encoder._matchFinder._blockSize = blockSize;
        }
        this._encoder._matchFinder._pointerToLastSafePosition = this._encoder._matchFinder._blockSize - keepSizeAfter;
    }
    _MovePos_1() {
        const matchFinder = this._compressor.chunker.encoder._matchFinder;
        let pointerToPostion;
        matchFinder._pos += 1;
        if (matchFinder._pos > matchFinder._posLimit) {
            pointerToPostion = matchFinder._bufferOffset + matchFinder._pos;
            if (pointerToPostion > matchFinder._pointerToLastSafePosition) {
                this._lzInWindow.moveBlock();
            }
            this._lzInWindow.readBlock();
        }
    }
    _Create_3(keepAddBufferBefore, keepAddBufferAfter) {
        const dictionarySize = this._encoder._dictionarySize;
        const numFastBytes = this._encoder._numFastBytes;
        if (isDictionarySizeBelowThreshold(dictionarySize)) {
            this._encoder._matchFinder._cutValue = setCutValue(numFastBytes);
            const windowReservSize = computeWindowReservSize(dictionarySize, keepAddBufferBefore, numFastBytes, keepAddBufferAfter);
            this._Create_4(dictionarySize + keepAddBufferBefore, numFastBytes + keepAddBufferAfter, windowReservSize);
            this._encoder._matchFinder._matchMaxLen = setMatchMaxLen(numFastBytes);
            ensureCyclicBuffer(this._encoder._matchFinder, dictionarySize);
            const { hashMask, hashSizeSum } = computeHashSize(dictionarySize, this._encoder._matchFinder.HASH_ARRAY);
            if (this._encoder._matchFinder.HASH_ARRAY) {
                this._encoder._matchFinder._hashMask = hashMask;
                const finalHashSizeSum = hashSizeSum + this._encoder._matchFinder.kFixHashSize;
                if (finalHashSizeSum !== this._encoder._matchFinder._hashSizeSum) {
                    this._encoder._matchFinder._hashSizeSum = finalHashSizeSum;
                    this._encoder._matchFinder._hash = initArray(finalHashSizeSum);
                }
            }
            else {
                if (hashSizeSum !== this._encoder._matchFinder._hashSizeSum) {
                    this._encoder._matchFinder._hashSizeSum = hashSizeSum;
                    this._encoder._matchFinder._hash = initArray(hashSizeSum);
                }
            }
        }
    }
    _GetMatches() {
        let count, cur, curMatch, curMatch2, curMatch3, cyclicPos, delta, hash2Value, hash3Value, hashValue, len, len0, len1, lenLimit, matchMinPos, maxLen, offset, pby1, ptr0, ptr1, temp;
        const matchFinder = this._compressor.chunker.encoder._matchFinder;
        const distances = this._compressor.chunker.encoder._matchDistances;
        if (matchFinder._pos + matchFinder._matchMaxLen <= matchFinder._streamPos) {
            lenLimit = matchFinder._matchMaxLen;
        }
        else {
            lenLimit = matchFinder._streamPos - matchFinder._pos;
            if (lenLimit < matchFinder.kMinMatchCheck) {
                this._MovePos_0();
                return 0;
            }
        }
        offset = 0;
        matchMinPos = matchFinder._pos > matchFinder._cyclicBufferSize
            ? matchFinder._pos - matchFinder._cyclicBufferSize
            : 0;
        cur = matchFinder._bufferOffset + matchFinder._pos;
        maxLen = 1;
        hash2Value = 0;
        hash3Value = 0;
        if (matchFinder.HASH_ARRAY) {
            temp = CRC32_TABLE[matchFinder._bufferBase[cur] & 0xFF] ^ (matchFinder._bufferBase[cur + 1] & 0xFF);
            hash2Value = temp & 0x3FF;
            temp ^= (matchFinder._bufferBase[cur + 2] & 0xFF) << 0x08;
            hash3Value = temp & 0xFFFF;
            hashValue = (temp ^ (CRC32_TABLE[matchFinder._bufferBase[cur + 3] & 0xFF] << 5)) & matchFinder._hashMask;
        }
        else {
            hashValue = (matchFinder._bufferBase[cur] & 0xFF) ^ ((matchFinder._bufferBase[cur + 1] & 0xFF) << 0x08);
        }
        curMatch = matchFinder._hash[matchFinder.kFixHashSize + hashValue] || 0;
        if (matchFinder.HASH_ARRAY) {
            curMatch2 = matchFinder._hash[hash2Value] || 0;
            curMatch3 = matchFinder._hash[0x400 + hash3Value] || 0;
            matchFinder._hash[hash2Value] = matchFinder._pos;
            matchFinder._hash[0x400 + hash3Value] = matchFinder._pos;
            if (curMatch2 > matchMinPos) {
                if (matchFinder._bufferBase[matchFinder._bufferOffset + curMatch2] == matchFinder._bufferBase[cur]) {
                    distances[offset++] = maxLen = 2;
                    distances[offset++] = matchFinder._pos - curMatch2 - 1;
                }
            }
            if (curMatch3 > matchMinPos) {
                if (matchFinder._bufferBase[matchFinder._bufferOffset + curMatch3] == matchFinder._bufferBase[cur]) {
                    if (curMatch3 == curMatch2) {
                        offset -= 2;
                    }
                    distances[offset++] = maxLen = 3;
                    distances[offset++] = matchFinder._pos - curMatch3 - 1;
                    curMatch2 = curMatch3;
                }
            }
            if (offset != 0 && curMatch2 == curMatch) {
                offset -= 2;
                maxLen = 1;
            }
        }
        matchFinder._hash[matchFinder.kFixHashSize + hashValue] = matchFinder._pos;
        ptr0 = (matchFinder._cyclicBufferPos << 1) + 1;
        ptr1 = matchFinder._cyclicBufferPos << 1;
        len0 = len1 = matchFinder.kNumHashDirectBytes;
        if (matchFinder.kNumHashDirectBytes != 0) {
            if (curMatch > matchMinPos) {
                if (matchFinder._bufferBase[matchFinder._bufferOffset + curMatch + matchFinder.kNumHashDirectBytes] != matchFinder._bufferBase[cur + matchFinder.kNumHashDirectBytes]) {
                    distances[offset++] = maxLen = matchFinder.kNumHashDirectBytes;
                    distances[offset++] = matchFinder._pos - curMatch - 1;
                }
            }
        }
        count = matchFinder._cutValue;
        while (1) {
            if (curMatch <= matchMinPos || count == 0) {
                count -= 1;
                matchFinder._son[ptr0] = matchFinder._son[ptr1] = 0;
                break;
            }
            delta = matchFinder._pos - curMatch;
            cyclicPos = (delta <= matchFinder._cyclicBufferPos
                ? matchFinder._cyclicBufferPos - delta
                : matchFinder._cyclicBufferPos - delta + matchFinder._cyclicBufferSize) << 1;
            pby1 = matchFinder._bufferOffset + curMatch;
            len = len0 < len1 ? len0 : len1;
            if (matchFinder._bufferBase[pby1 + len] == matchFinder._bufferBase[cur + len]) {
                while ((len += 1) != lenLimit) {
                    if (matchFinder._bufferBase[pby1 + len] != matchFinder._bufferBase[cur + len]) {
                        break;
                    }
                }
                if (maxLen < len) {
                    distances[offset++] = maxLen = len;
                    distances[offset++] = delta - 1;
                    if (len == lenLimit) {
                        matchFinder._son[ptr1] = matchFinder._son[cyclicPos];
                        matchFinder._son[ptr0] = matchFinder._son[cyclicPos + 1];
                        break;
                    }
                }
            }
            if ((matchFinder._bufferBase[pby1 + len] & 0xFF) < (matchFinder._bufferBase[cur + len] & 0xFF)) {
                matchFinder._son[ptr1] = curMatch;
                ptr1 = cyclicPos + 1;
                curMatch = matchFinder._son[ptr1];
                len1 = len;
            }
            else {
                matchFinder._son[ptr0] = curMatch;
                ptr0 = cyclicPos;
                curMatch = matchFinder._son[ptr0];
                len0 = len;
            }
        }
        this._MovePos_0();
        return offset;
    }
    _Init_5() {
        this._compressor.chunker.encoder._matchFinder._bufferOffset = 0;
        this._compressor.chunker.encoder._matchFinder._pos = 0;
        this._compressor.chunker.encoder._matchFinder._streamPos = 0;
        this._compressor.chunker.encoder._matchFinder._streamEndWasReached = 0;
        this._lzInWindow.readBlock();
        this._compressor.chunker.encoder._matchFinder._cyclicBufferPos = 0;
        this._lzInWindow.reduceOffsets(-1);
    }
    _MovePos_0() {
        let subValue;
        const matchFinder = this._compressor.chunker.encoder._matchFinder;
        if ((matchFinder._cyclicBufferPos += 1) >= matchFinder._cyclicBufferSize) {
            matchFinder._cyclicBufferPos = 0;
        }
        this._MovePos_1();
        if (matchFinder._pos == DICTIONARY_SIZE_THRESHOLD) {
            subValue = matchFinder._pos - matchFinder._cyclicBufferSize;
            this._NormalizeLinks(matchFinder._cyclicBufferSize * 2, subValue);
            this._NormalizeLinks(matchFinder._hashSizeSum, subValue);
            this._lzInWindow.reduceOffsets(subValue);
        }
    }
    /**
     * This is only called after reading one whole gigabyte.
     */
    _NormalizeLinks(numItems, subValue) {
        const items = this._compressor.chunker.encoder._matchFinder._son;
        for (let i = 0, value; i < numItems; ++i) {
            value = items[i] || 0;
            if (value <= subValue) {
                value = 0;
            }
            else {
                value -= subValue;
            }
            items[i] = value;
        }
    }
    _SetType(binTree, numHashBytes) {
        binTree.HASH_ARRAY = numHashBytes > 2;
        if (binTree.HASH_ARRAY) {
            binTree.kNumHashDirectBytes = 0;
            binTree.kMinMatchCheck = 4;
            binTree.kFixHashSize = 66560;
        }
        else {
            binTree.kNumHashDirectBytes = 2;
            binTree.kMinMatchCheck = 3;
            binTree.kFixHashSize = 0;
        }
    }
    _Skip(num) {
        const matchFinder = this._compressor.chunker.encoder._matchFinder;
        let count, cur, curMatch, cyclicPos, delta, hash2Value, hash3Value, hashValue, len, len0, len1, lenLimit, matchMinPos, pby1, ptr0, ptr1, temp;
        do {
            if (matchFinder._pos + matchFinder._matchMaxLen <= matchFinder._streamPos) {
                lenLimit = matchFinder._matchMaxLen;
            }
            else {
                lenLimit = matchFinder._streamPos - matchFinder._pos;
                if (lenLimit < matchFinder.kMinMatchCheck) {
                    this._MovePos_0();
                    continue;
                }
            }
            matchMinPos = matchFinder._pos > matchFinder._cyclicBufferSize
                ? matchFinder._pos - matchFinder._cyclicBufferSize
                : 0;
            cur = matchFinder._bufferOffset + matchFinder._pos;
            if (matchFinder.HASH_ARRAY) {
                temp = CRC32_TABLE[matchFinder._bufferBase[cur] & 0xFF] ^ (matchFinder._bufferBase[cur + 1] & 0xFF);
                hash2Value = temp & 0x3FF;
                matchFinder._hash[hash2Value] = matchFinder._pos;
                temp ^= (matchFinder._bufferBase[cur + 2] & 0xFF) << 0x08;
                hash3Value = temp & 0xFFFF;
                matchFinder._hash[0x400 + hash3Value] = matchFinder._pos;
                hashValue = (temp ^ (CRC32_TABLE[matchFinder._bufferBase[cur + 3] & 0xFF] << 5)) & matchFinder._hashMask;
            }
            else {
                hashValue = (matchFinder._bufferBase[cur] & 0xFF) ^ ((matchFinder._bufferBase[cur + 1] & 0xFF) << 0x08);
            }
            curMatch = matchFinder._hash[matchFinder.kFixHashSize + hashValue];
            matchFinder._hash[matchFinder.kFixHashSize + hashValue] = matchFinder._pos;
            ptr0 = (matchFinder._cyclicBufferPos << 1) + 1;
            ptr1 = matchFinder._cyclicBufferPos << 1;
            len0 = len1 = matchFinder.kNumHashDirectBytes;
            count = matchFinder._cutValue;
            while (1) {
                if (curMatch <= matchMinPos || count == 0) {
                    count -= 1;
                    matchFinder._son[ptr0] = matchFinder._son[ptr1] = 0;
                    break;
                }
                delta = matchFinder._pos - curMatch;
                cyclicPos = (delta <= matchFinder._cyclicBufferPos
                    ? matchFinder._cyclicBufferPos - delta
                    : matchFinder._cyclicBufferPos - delta + matchFinder._cyclicBufferSize) << 1;
                pby1 = matchFinder._bufferOffset + curMatch;
                len = len0 < len1 ? len0 : len1;
                if (matchFinder._bufferBase[pby1 + len] == matchFinder._bufferBase[cur + len]) {
                    while ((len += 1) != lenLimit) {
                        if (matchFinder._bufferBase[pby1 + len] != matchFinder._bufferBase[cur + len]) {
                            break;
                        }
                    }
                    if (len == lenLimit) {
                        matchFinder._son[ptr1] = matchFinder._son[cyclicPos];
                        matchFinder._son[ptr0] = matchFinder._son[cyclicPos + 1];
                        break;
                    }
                }
                if ((matchFinder._bufferBase[pby1 + len] & 0xFF) < (matchFinder._bufferBase[cur + len] & 0xFF)) {
                    matchFinder._son[ptr1] = curMatch;
                    ptr1 = cyclicPos + 1;
                    curMatch = matchFinder._son[ptr1];
                    len1 = len;
                }
                else {
                    matchFinder._son[ptr0] = curMatch;
                    ptr0 = cyclicPos;
                    curMatch = matchFinder._son[ptr0];
                    len0 = len;
                }
            }
            this._MovePos_0();
        } while ((num -= 1) != 0);
    }
    _CodeInChunks(inStream, outSize) {
        this._decoder.rangeDecoder.stream = inStream;
        this._decoder.flush();
        this._decoder.outWindow.stream = null;
        this._decoder.outWindow.stream = this._decompressor.output;
        this._Init_1();
        this._decoder.state = 0;
        this._decoder.rep0 = 0;
        this._decoder.rep1 = 0;
        this._decoder.rep2 = 0;
        this._decoder.rep3 = 0;
        this._decoder.outSize = outSize;
        this._decoder.nowPos64 = P0_LONG_LIT;
        this._decoder.prevByte = 0;
        // Create and return decoder chunker
        const decoderChunker = new DecoderChunker(this._decoder);
        decoderChunker.alive = 1;
        return decoderChunker;
    }
    _Init_1() {
        this._decoder.init();
    }
    _Backward(cur) {
        const encoder = this._compressor.chunker.encoder;
        let backCur, backMem, posMem, posPrev;
        encoder._optimumEndIndex = cur;
        posMem = encoder._optimum[cur].posPrev;
        backMem = encoder._optimum[cur].backPrev;
        do {
            if (encoder._optimum[cur].prev1IsChar) {
                this._MakeAsChar(encoder._optimum[posMem]);
                encoder._optimum[posMem].posPrev = posMem - 1;
                if (encoder._optimum[cur].prev2) {
                    encoder._optimum[posMem - 1].prev1IsChar = 0;
                    encoder._optimum[posMem - 1].posPrev = encoder._optimum[cur].posPrev2;
                    encoder._optimum[posMem - 1].backPrev = encoder._optimum[cur].backPrev2;
                }
            }
            posPrev = posMem;
            backCur = backMem;
            backMem = encoder._optimum[posPrev].backPrev;
            posMem = encoder._optimum[posPrev].posPrev;
            encoder._optimum[posPrev].backPrev = backCur;
            encoder._optimum[posPrev].posPrev = cur;
            cur = posPrev;
        } while (cur > 0);
        encoder.backRes = encoder._optimum[0].backPrev;
        encoder._optimumCurrentIndex = encoder._optimum[0].posPrev;
        return encoder._optimumCurrentIndex;
    }
    _CodeOneBlock() {
        let baseVal, complexState, curByte, distance, footerBits, len, lenToPosState, matchByte, pos, posReduced, posSlot, posState, progressPosValuePrev, subCoder;
        this._compressor.chunker.encoder.processedInSize[0] = P0_LONG_LIT;
        this._compressor.chunker.encoder.processedOutSize[0] = P0_LONG_LIT;
        this._compressor.chunker.encoder.finished[0] = 1;
        progressPosValuePrev = this._compressor.chunker.encoder.nowPos64;
        if (this._compressor.chunker.encoder._inStream) {
            this._compressor.chunker.encoder._matchFinder._stream = this._compressor.chunker.encoder._inStream;
            this._Init_5();
            this._compressor.chunker.encoder._needReleaseMFStream = 1;
            this._compressor.chunker.encoder._inStream = null;
        }
        if (this._compressor.chunker.encoder._finished) {
            return;
        }
        this._compressor.chunker.encoder._finished = 1;
        if (compare64(this._compressor.chunker.encoder.nowPos64, P0_LONG_LIT) === 0) {
            if (!this._lzInWindow.getNumAvailableBytes()) {
                this._Flush(lowBits64(this._compressor.chunker.encoder.nowPos64));
                return;
            }
            this._ReadMatchDistances();
            posState = lowBits64(this._compressor.chunker.encoder.nowPos64) & this._compressor.chunker.encoder._posStateMask;
            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isMatch, (this._compressor.chunker.encoder._state << 4) + posState, 0);
            this._compressor.chunker.encoder._state = stateUpdateChar(this._compressor.chunker.encoder._state);
            curByte = this._lzInWindow.getIndexByte(-this._compressor.chunker.encoder._additionalOffset);
            this._compressor.chunker.encoder.encodeLiteral(this._LZMA_Encoder_GetSubCoder(lowBits64(this._compressor.chunker.encoder.nowPos64), this._compressor.chunker.encoder._previousByte), curByte);
            this._compressor.chunker.encoder._previousByte = curByte;
            this._compressor.chunker.encoder._additionalOffset -= 1;
            this._compressor.chunker.encoder.nowPos64 = add64(this._compressor.chunker.encoder.nowPos64, P1_LONG_LIT);
        }
        if (!this._lzInWindow.getNumAvailableBytes()) {
            this._Flush(lowBits64(this._compressor.chunker.encoder.nowPos64));
            return;
        }
        while (1) {
            len = this._GetOptimum(lowBits64(this._compressor.chunker.encoder.nowPos64));
            pos = this._compressor.chunker.encoder.backRes;
            posState = lowBits64(this._compressor.chunker.encoder.nowPos64) & this._compressor.chunker.encoder._posStateMask;
            complexState = (this._compressor.chunker.encoder._state << 4) + posState;
            if (len == 1 && pos == -1) {
                this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isMatch, complexState, 0);
                curByte = this._lzInWindow.getIndexByte(-this._compressor.chunker.encoder._additionalOffset);
                subCoder = this._LZMA_Encoder_GetSubCoder(lowBits64(this._compressor.chunker.encoder.nowPos64), this._compressor.chunker.encoder._previousByte);
                if (this._compressor.chunker.encoder._state < 7) {
                    this._compressor.chunker.encoder.encodeLiteral(subCoder, curByte);
                }
                else {
                    matchByte = this._lzInWindow.getIndexByte(-this._compressor.chunker.encoder._repDistances[0]
                        - 1
                        - this._compressor.chunker.encoder._additionalOffset);
                    this._compressor.chunker.encoder.encodeMatched(subCoder, matchByte, curByte);
                }
                this._compressor.chunker.encoder._previousByte = curByte;
                this._compressor.chunker.encoder._state = stateUpdateChar(this._compressor.chunker.encoder._state);
            }
            else {
                this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isMatch, complexState, 1);
                if (pos < 4) {
                    this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRep, this._compressor.chunker.encoder._state, 1);
                    if (!pos) {
                        this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRepG0, this._compressor.chunker.encoder._state, 0);
                        if (len == 1) {
                            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRep0Long, complexState, 0);
                        }
                        else {
                            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRep0Long, complexState, 1);
                        }
                    }
                    else {
                        this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRepG0, this._compressor.chunker.encoder._state, 1);
                        if (pos == 1) {
                            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRepG1, this._compressor.chunker.encoder._state, 0);
                        }
                        else {
                            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRepG1, this._compressor.chunker.encoder._state, 1);
                            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRepG2, this._compressor.chunker.encoder._state, pos - 2);
                        }
                    }
                    if (len == 1) {
                        this._compressor.chunker.encoder._state = this._compressor.chunker.encoder._state < 7 ? 9 : 11;
                    }
                    else {
                        this._compressor.chunker.encoder.encodeLength(this._compressor.chunker.encoder._repMatchLenEncoder, len - 2, posState);
                        this._compressor.chunker.encoder._state = this._compressor.chunker.encoder._state < 7
                            ? 0x08
                            : 11;
                    }
                    distance = this._compressor.chunker.encoder._repDistances[pos];
                    if (pos != 0) {
                        const encoder = this._compressor.chunker.encoder;
                        for (let i = pos; i >= 1; --i) {
                            encoder._repDistances[i] = encoder._repDistances[i - 1];
                        }
                        encoder._repDistances[0] = distance;
                    }
                }
                else {
                    this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._isRep, this._compressor.chunker.encoder._state, 0);
                    this._compressor.chunker.encoder._state = this._compressor.chunker.encoder._state < 7 ? 7 : 10;
                    this._compressor.chunker.encoder.encodeLength(this._compressor.chunker.encoder._lenEncoder, len - 0x02, posState);
                    pos -= 0x04;
                    posSlot = this._compressor.chunker.encoder.getPosSlot(pos);
                    lenToPosState = getLenToPosState(len);
                    this._compressor.chunker.encoder.encodeBitTree(this._compressor.chunker.encoder._posSlotEncoder[lenToPosState], posSlot);
                    if (posSlot >= 0x04) {
                        footerBits = (posSlot >> 0x01) - 0x01;
                        baseVal = (0x02 | (posSlot & 0x01)) << footerBits;
                        posReduced = pos - baseVal;
                        if (posSlot < 0x0E) {
                            this._compressor.chunker.encoder.reverseEncodeRange(baseVal - posSlot - 0x01, footerBits, posReduced);
                        }
                        else {
                            this._compressor.chunker.encoder.encodeDirectBits(posReduced >> 0x04, footerBits - 4);
                            this._compressor.chunker.encoder.reverseEncode(posReduced & 0x0F);
                            this._compressor.chunker.encoder._alignPriceCount += 1;
                        }
                    }
                    distance = pos;
                    const encoder2 = this._compressor.chunker.encoder;
                    for (let i = 3; i >= 1; --i) {
                        encoder2._repDistances[i] = encoder2._repDistances[i - 1];
                    }
                    encoder2._repDistances[0] = distance;
                    encoder2._matchPriceCount += 0x01;
                }
                this._compressor.chunker.encoder._previousByte = this._lzInWindow.getIndexByte(len - 1 - this._compressor.chunker.encoder._additionalOffset);
            }
            this._compressor.chunker.encoder._additionalOffset -= len;
            this._compressor.chunker.encoder.nowPos64 = add64(this._compressor.chunker.encoder.nowPos64, fromInt64(len));
            if (!this._compressor.chunker.encoder._additionalOffset) {
                if (this._compressor.chunker.encoder._matchPriceCount >= 0x80) {
                    this._compressor.chunker.encoder.fillDistancesPrices();
                }
                if (this._compressor.chunker.encoder._alignPriceCount >= 0x10) {
                    this._FillAlignPrices(this._compressor.chunker.encoder);
                }
                this._compressor.chunker.encoder.processedInSize[0] = this._compressor.chunker.encoder.nowPos64;
                this._compressor.chunker.encoder.processedOutSize[0] = this._GetProcessedSizeAdd();
                if (!this._lzInWindow.getNumAvailableBytes()) {
                    this._Flush(lowBits64(this._compressor.chunker.encoder.nowPos64));
                    return;
                }
                if (compare64(sub64(this._compressor.chunker.encoder.nowPos64, progressPosValuePrev), [0x1000, 0]) >= 0) {
                    this._compressor.chunker.encoder._finished = 0;
                    this._compressor.chunker.encoder.finished[0] = 0;
                    return;
                }
            }
        }
    }
    _Create_2() {
        let binTree, numHashBytes;
        if (!this._encoder._matchFinder) {
            binTree = {};
            numHashBytes = 4;
            if (!this._encoder._matchFinderType) {
                numHashBytes = 2;
            }
            this._SetType(binTree, numHashBytes);
            this._encoder._matchFinder = binTree;
            this._lzInWindow = new LzInWindow(binTree);
        }
        this._encoder.createLiteralEncoder();
        if (this._encoder._dictionarySize == this._encoder._dictionarySizePrev
            && this._encoder._numFastBytesPrev == this._encoder._numFastBytes) {
            return;
        }
        this._Create_3(0x1000, 0x0112);
        this._encoder._dictionarySizePrev = this._encoder._dictionarySize;
        this._encoder._numFastBytesPrev = this._encoder._numFastBytes;
    }
    _Encoder() {
        for (let i = 0; i < 0x1000; ++i) {
            this._encoder._optimum[i] = {};
        }
        for (let i = 0; i < 4; ++i) {
            this._encoder._posSlotEncoder[i] = createBitTree(6);
        }
    }
    _FillAlignPrices(encoder) {
        for (let i = 0; i < 16; ++i) {
            encoder._alignPrices[i] = this._ReverseGetPrice(encoder._posAlignEncoder, i);
        }
        encoder._alignPriceCount = 0;
    }
    _Flush(nowPos) {
        this._ReleaseMFStream();
        this._compressor.chunker.encoder.writeEndMarker(nowPos & this._compressor.chunker.encoder._posStateMask);
        for (let i = 0; i < 5; ++i) {
            this._compressor.chunker.encoder.shiftLow();
        }
    }
    _GetOptimum(position) {
        let cur, curAnd1Price, curAndLenCharPrice, curAndLenPrice, curBack, curPrice, currentByte, distance, len, lenEnd, lenMain, lenTest, lenTest2, lenTestTemp, matchByte, matchPrice, newLen, nextIsChar, nextMatchPrice, nextOptimum, nextRepMatchPrice, normalMatchPrice, numAvailableBytes, numAvailableBytesFull, numDistancePairs, offs, offset, opt, optimum, pos, posPrev, posState, posStateNext, price_4, repIndex, repLen, repMatchPrice, repMaxIndex, shortRepPrice, startLen, state, state2, t, price, price_0, price_1, price_2, price_3, lenRes;
        const encoder = this._compressor.chunker.encoder;
        if (encoder._optimumEndIndex != encoder._optimumCurrentIndex) {
            lenRes = encoder._optimum[encoder._optimumCurrentIndex].posPrev - encoder._optimumCurrentIndex;
            encoder.backRes = encoder._optimum[encoder._optimumCurrentIndex].backPrev;
            encoder._optimumCurrentIndex = encoder._optimum[encoder._optimumCurrentIndex].posPrev;
            return lenRes;
        }
        encoder._optimumCurrentIndex = encoder._optimumEndIndex = 0;
        if (encoder._longestMatchWasFound) {
            lenMain = encoder._longestMatchLength;
            encoder._longestMatchWasFound = 0;
        }
        else {
            lenMain = this._ReadMatchDistances();
        }
        numDistancePairs = encoder._numDistancePairs;
        numAvailableBytes = this._lzInWindow.getNumAvailableBytes() + 1;
        if (numAvailableBytes < 2) {
            encoder.backRes = -1;
            return 1;
        }
        if (numAvailableBytes > 0x0111) {
            numAvailableBytes = 0x0111;
        }
        repMaxIndex = 0;
        for (let i = 0; i < 4; ++i) {
            encoder.reps[i] = encoder._repDistances[i];
            encoder.repLens[i] = this._lzInWindow.getMatchLen(-1, encoder.reps[i], 0x0111);
            if (encoder.repLens[i] > encoder.repLens[repMaxIndex]) {
                repMaxIndex = i;
            }
        }
        if (encoder.repLens[repMaxIndex] >= encoder._numFastBytes) {
            encoder.backRes = repMaxIndex;
            lenRes = encoder.repLens[repMaxIndex];
            this._MovePos(lenRes - 1);
            return lenRes;
        }
        if (lenMain >= encoder._numFastBytes) {
            encoder.backRes = this._compressor.chunker.encoder._matchDistances[numDistancePairs - 1] + 4;
            this._MovePos(lenMain - 1);
            return lenMain;
        }
        currentByte = this._lzInWindow.getIndexByte(-1);
        matchByte = this._lzInWindow.getIndexByte(-encoder._repDistances[0] - 1 - 1);
        if (lenMain < 2 && currentByte != matchByte && encoder.repLens[repMaxIndex] < 2) {
            encoder.backRes = -1;
            return 1;
        }
        encoder._optimum[0].state = encoder._state;
        posState = position & encoder._posStateMask;
        encoder._optimum[1].price = PROB_PRICES[(encoder._isMatch[(encoder._state << 4) + posState]) >>> 2] + this._RangeCoder_Encoder_GetPrice_0(this._LZMA_Encoder_GetSubCoder(position, encoder._previousByte), encoder._state >= 7, matchByte, currentByte);
        this._MakeAsChar(encoder._optimum[1]);
        matchPrice = PROB_PRICES[(2048 - encoder._isMatch[(encoder._state << 4) + posState])
            >>> 2];
        repMatchPrice = matchPrice + PROB_PRICES[(2048 - encoder._isRep[encoder._state]) >>> 2];
        if (matchByte == currentByte) {
            shortRepPrice = repMatchPrice + this._GetRepLen1Price(posState);
            if (shortRepPrice < encoder._optimum[1].price) {
                encoder._optimum[1].price = shortRepPrice;
                this._MakeAsShortRep(encoder._optimum[1]);
            }
        }
        lenEnd = lenMain >= encoder.repLens[repMaxIndex]
            ? lenMain
            : encoder.repLens[repMaxIndex];
        if (lenEnd < 2) {
            encoder.backRes = encoder._optimum[1].backPrev;
            return 1;
        }
        encoder._optimum[1].posPrev = 0;
        encoder._optimum[0].backs0 = encoder.reps[0];
        encoder._optimum[0].backs1 = encoder.reps[1];
        encoder._optimum[0].backs2 = encoder.reps[2];
        encoder._optimum[0].backs3 = encoder.reps[3];
        len = lenEnd;
        do {
            encoder._optimum[len].price = INFINITY_PRICE;
            len -= 1;
        } while (len >= 2);
        for (let i = 0; i < 4; ++i) {
            repLen = encoder.repLens[i];
            if (repLen < 2) {
                continue;
            }
            price_4 = repMatchPrice + this._GetPureRepPrice(i, encoder._state, posState);
            do {
                curAndLenPrice = price_4 + encoder._repMatchLenEncoder.getPrice(repLen - 2, posState);
                optimum = encoder._optimum[repLen];
                if (curAndLenPrice < optimum.price) {
                    optimum.price = curAndLenPrice;
                    optimum.posPrev = 0;
                    optimum.backPrev = i;
                    optimum.prev1IsChar = 0;
                }
            } while ((repLen -= 1) >= 2);
        }
        normalMatchPrice = matchPrice
            + PROB_PRICES[(encoder._isRep[encoder._state]) >>> 2];
        len = encoder.repLens[0] >= 2 ? encoder.repLens[0] + 1 : 2;
        if (len <= lenMain) {
            offs = 0;
            while (len > encoder._matchDistances[offs]) {
                offs += 2;
            }
            for (;; len += 1) {
                distance = encoder._matchDistances[offs + 1];
                curAndLenPrice = normalMatchPrice + this._LZMA_Encoder_GetPosLenPrice(distance, len, posState);
                optimum = encoder._optimum[len];
                if (curAndLenPrice < optimum.price) {
                    optimum.price = curAndLenPrice;
                    optimum.posPrev = 0;
                    optimum.backPrev = distance + 4;
                    optimum.prev1IsChar = 0;
                }
                if (len == encoder._matchDistances[offs]) {
                    offs += 2;
                    if (offs == numDistancePairs) {
                        break;
                    }
                }
            }
        }
        cur = 0;
        while (1) {
            ++cur;
            if (cur == lenEnd) {
                return this._Backward(cur);
            }
            newLen = this._ReadMatchDistances();
            numDistancePairs = encoder._numDistancePairs;
            if (newLen >= encoder._numFastBytes) {
                encoder._longestMatchLength = newLen;
                encoder._longestMatchWasFound = 0x01;
                return this._Backward(cur);
            }
            position += 0x01;
            posPrev = encoder._optimum[cur].posPrev;
            if (encoder._optimum[cur].prev1IsChar) {
                posPrev -= 0x01;
                if (encoder._optimum[cur].prev2) {
                    state = encoder._optimum[encoder._optimum[cur].posPrev2].state;
                    if (encoder._optimum[cur].backPrev2 < 0x04) {
                        state = (state < 0x07) ? 0x08 : 0x0B;
                    }
                    else {
                        state = (state < 0x07) ? 0x07 : 0x0A;
                    }
                }
                else {
                    state = encoder._optimum[posPrev].state;
                }
                state = stateUpdateChar(state);
            }
            else {
                state = encoder._optimum[posPrev].state;
            }
            if (posPrev == cur - 1) {
                if (!encoder._optimum[cur].backPrev) {
                    state = state < 7 ? 9 : 11;
                }
                else {
                    state = stateUpdateChar(state);
                }
            }
            else {
                if (encoder._optimum[cur].prev1IsChar
                    && encoder._optimum[cur].prev2) {
                    posPrev = encoder._optimum[cur].posPrev2;
                    pos = encoder._optimum[cur].backPrev2;
                    state = state < 0x07 ? 0x08 : 0x0B;
                }
                else {
                    pos = encoder._optimum[cur].backPrev;
                    if (pos < 4) {
                        state = state < 0x07 ? 0x08 : 0x0B;
                    }
                    else {
                        state = state < 0x07 ? 0x07 : 0x0A;
                    }
                }
                opt = encoder._optimum[posPrev];
                if (pos < 4) {
                    if (!pos) {
                        encoder.reps[0] = opt.backs0;
                        encoder.reps[1] = opt.backs1;
                        encoder.reps[2] = opt.backs2;
                        encoder.reps[3] = opt.backs3;
                    }
                    else if (pos == 1) {
                        encoder.reps[0] = opt.backs1;
                        encoder.reps[1] = opt.backs0;
                        encoder.reps[2] = opt.backs2;
                        encoder.reps[3] = opt.backs3;
                    }
                    else if (pos == 2) {
                        encoder.reps[0] = opt.backs2;
                        encoder.reps[1] = opt.backs0;
                        encoder.reps[2] = opt.backs1;
                        encoder.reps[3] = opt.backs3;
                    }
                    else {
                        encoder.reps[0] = opt.backs3;
                        encoder.reps[1] = opt.backs0;
                        encoder.reps[2] = opt.backs1;
                        encoder.reps[3] = opt.backs2;
                    }
                }
                else {
                    encoder.reps[0] = pos - 4;
                    encoder.reps[1] = opt.backs0;
                    encoder.reps[2] = opt.backs1;
                    encoder.reps[3] = opt.backs2;
                }
            }
            encoder._optimum[cur].state = state;
            encoder._optimum[cur].backs0 = encoder.reps[0];
            encoder._optimum[cur].backs1 = encoder.reps[1];
            encoder._optimum[cur].backs2 = encoder.reps[2];
            encoder._optimum[cur].backs3 = encoder.reps[3];
            curPrice = encoder._optimum[cur].price;
            currentByte = this._lzInWindow.getIndexByte(-0x01);
            matchByte = this._lzInWindow.getIndexByte(-encoder.reps[0] - 1 - 1);
            posState = position & encoder._posStateMask;
            curAnd1Price = curPrice
                + PROB_PRICES[(encoder._isMatch[(state << 0x04) + posState]) >>> 2]
                + this._RangeCoder_Encoder_GetPrice_0(this._LZMA_Encoder_GetSubCoder(position, this._lzInWindow.getIndexByte(-2)), state >= 7, matchByte, currentByte);
            nextOptimum = encoder._optimum[cur + 1];
            nextIsChar = 0;
            if (curAnd1Price < nextOptimum.price) {
                nextOptimum.price = curAnd1Price;
                nextOptimum.posPrev = cur;
                nextOptimum.backPrev = -0x01;
                nextOptimum.prev1IsChar = 0;
                nextIsChar = 1;
            }
            matchPrice = curPrice + PROB_PRICES[(2048 - encoder._isMatch[(state << 4) + posState]) >>> 2];
            repMatchPrice = matchPrice + PROB_PRICES[(2048 - encoder._isRep[state]) >>> 2];
            if (matchByte == currentByte && !(nextOptimum.posPrev < cur && !nextOptimum.backPrev)) {
                shortRepPrice = repMatchPrice
                    + (PROB_PRICES[(encoder._isRepG0[state]) >>> 0x02] + PROB_PRICES[(encoder._isRep0Long[(state << 0x04) + posState]) >>> 0x02]);
                if (shortRepPrice <= nextOptimum.price) {
                    nextOptimum.price = shortRepPrice;
                    nextOptimum.posPrev = cur;
                    nextOptimum.backPrev = 0;
                    nextOptimum.prev1IsChar = 0;
                    nextIsChar = 1;
                }
            }
            numAvailableBytesFull = this._lzInWindow.getNumAvailableBytes() + 1;
            numAvailableBytesFull = 0xFFF - cur < numAvailableBytesFull
                ? 0xFFF - cur
                : numAvailableBytesFull;
            numAvailableBytes = numAvailableBytesFull;
            if (numAvailableBytes < 2) {
                continue;
            }
            if (numAvailableBytes > encoder._numFastBytes) {
                numAvailableBytes = encoder._numFastBytes;
            }
            if (!nextIsChar && matchByte != currentByte) {
                t = Math.min(numAvailableBytesFull - 1, encoder._numFastBytes);
                lenTest2 = this._lzInWindow.getMatchLen(0, encoder.reps[0], t);
                if (lenTest2 >= 2) {
                    state2 = stateUpdateChar(state);
                    posStateNext = position + 1 & encoder._posStateMask;
                    nextRepMatchPrice = curAnd1Price
                        + PROB_PRICES[(2048 - encoder._isMatch[(state2 << 4) + posStateNext]) >>> 2]
                        + PROB_PRICES[(2048 - encoder._isRep[state2]) >>> 2];
                    offset = cur + 1 + lenTest2;
                    while (lenEnd < offset) {
                        encoder._optimum[lenEnd += 1].price = INFINITY_PRICE;
                    }
                    curAndLenPrice = nextRepMatchPrice + (price = encoder._repMatchLenEncoder.getPrice(lenTest2 - 2, posStateNext),
                        price + this._GetPureRepPrice(0, state2, posStateNext));
                    optimum = encoder._optimum[offset];
                    if (curAndLenPrice < optimum.price) {
                        optimum.price = curAndLenPrice;
                        optimum.posPrev = cur + 1;
                        optimum.backPrev = 0;
                        optimum.prev1IsChar = 1;
                        optimum.prev2 = 0;
                    }
                }
            }
            startLen = 0x02;
            for (repIndex = 0; repIndex < 4; ++repIndex) {
                lenTest = this._lzInWindow.getMatchLen(-0x01, encoder.reps[repIndex], numAvailableBytes);
                if (lenTest < 2) {
                    continue;
                }
                lenTestTemp = lenTest;
                do {
                    while (lenEnd < cur + lenTest) {
                        encoder._optimum[lenEnd += 1].price = INFINITY_PRICE;
                    }
                    curAndLenPrice = repMatchPrice + (price_0 = encoder._repMatchLenEncoder.getPrice(lenTest - 2, posState),
                        price_0 + this._GetPureRepPrice(repIndex, state, posState));
                    optimum = encoder._optimum[cur + lenTest];
                    if (curAndLenPrice < optimum.price) {
                        optimum.price = curAndLenPrice;
                        optimum.posPrev = cur;
                        optimum.backPrev = repIndex;
                        optimum.prev1IsChar = 0;
                    }
                } while ((lenTest -= 1) >= 2);
                lenTest = lenTestTemp;
                if (!repIndex) {
                    startLen = lenTest + 1;
                }
                if (lenTest < numAvailableBytesFull) {
                    t = Math.min(numAvailableBytesFull - 1 - lenTest, encoder._numFastBytes);
                    lenTest2 = this._lzInWindow.getMatchLen(lenTest, encoder.reps[repIndex], t);
                    if (lenTest2 >= 2) {
                        state2 = state < 7 ? 0x08 : 11;
                        posStateNext = position + lenTest & encoder._posStateMask;
                        curAndLenCharPrice = repMatchPrice
                            + (price_1 = encoder._repMatchLenEncoder.getPrice(lenTest - 2, posState), price_1 + this._GetPureRepPrice(repIndex, state, posState))
                            + PROB_PRICES[(encoder._isMatch[(state2 << 4) + posStateNext]) >>> 2]
                            + this._RangeCoder_Encoder_GetPrice_0(this._LZMA_Encoder_GetSubCoder(position + lenTest, this._lzInWindow.getIndexByte(lenTest - 1 - 1)), true, this._lzInWindow.getIndexByte(lenTest - 1 - (encoder.reps[repIndex] + 1)), this._lzInWindow.getIndexByte(lenTest - 1));
                        state2 = stateUpdateChar(state2);
                        posStateNext = position + lenTest + 1 & encoder._posStateMask;
                        nextMatchPrice = curAndLenCharPrice + PROB_PRICES[(2048 - encoder._isMatch[(state2 << 4) + posStateNext]) >>> 2];
                        nextRepMatchPrice = nextMatchPrice + PROB_PRICES[(2048 - encoder._isRep[state2]) >>> 2];
                        offset = lenTest + 1 + lenTest2;
                        while (lenEnd < cur + offset) {
                            encoder._optimum[lenEnd += 1].price = INFINITY_PRICE;
                        }
                        curAndLenPrice = nextRepMatchPrice + (price_2 = encoder._repMatchLenEncoder.getPrice(lenTest2 - 2, posStateNext), price_2 + this._GetPureRepPrice(0, state2, posStateNext));
                        optimum = encoder._optimum[cur + offset];
                        if (curAndLenPrice < optimum.price) {
                            optimum.price = curAndLenPrice;
                            optimum.posPrev = cur + lenTest + 1;
                            optimum.backPrev = 0;
                            optimum.prev1IsChar = 1;
                            optimum.prev2 = 1;
                            optimum.posPrev2 = cur;
                            optimum.backPrev2 = repIndex;
                        }
                    }
                }
            }
            if (newLen > numAvailableBytes) {
                newLen = numAvailableBytes;
                for (numDistancePairs = 0; newLen > encoder._matchDistances[numDistancePairs]; numDistancePairs += 2) { }
                encoder._matchDistances[numDistancePairs] = newLen;
                numDistancePairs += 2;
            }
            if (newLen >= startLen) {
                normalMatchPrice = matchPrice + PROB_PRICES[(encoder._isRep[state]) >>> 2];
                while (lenEnd < cur + newLen) {
                    encoder._optimum[lenEnd += 1].price = INFINITY_PRICE;
                }
                offs = 0;
                while (startLen > encoder._matchDistances[offs]) {
                    offs += 2;
                }
                for (lenTest = startLen;; lenTest += 1) {
                    curBack = encoder._matchDistances[offs + 1];
                    curAndLenPrice = normalMatchPrice + this._LZMA_Encoder_GetPosLenPrice(curBack, lenTest, posState);
                    optimum = encoder._optimum[cur + lenTest];
                    if (curAndLenPrice < optimum.price) {
                        optimum.price = curAndLenPrice;
                        optimum.posPrev = cur;
                        optimum.backPrev = curBack + 4;
                        optimum.prev1IsChar = 0;
                    }
                    if (lenTest == encoder._matchDistances[offs]) {
                        if (lenTest < numAvailableBytesFull) {
                            t = Math.min(numAvailableBytesFull - 1 - lenTest, encoder._numFastBytes);
                            lenTest2 = this._lzInWindow.getMatchLen(lenTest, curBack, t);
                            if (lenTest2 >= 2) {
                                state2 = state < 7 ? 7 : 10;
                                posStateNext = position + lenTest & encoder._posStateMask;
                                curAndLenCharPrice = curAndLenPrice
                                    + PROB_PRICES[(encoder._isMatch[(state2 << 4) + posStateNext]) >>> 2]
                                    + this._RangeCoder_Encoder_GetPrice_0(this._LZMA_Encoder_GetSubCoder(position + lenTest, this._lzInWindow.getIndexByte(lenTest - 1 - 1)), true, this._lzInWindow.getIndexByte(lenTest - (curBack + 1) - 1), this._lzInWindow.getIndexByte(lenTest - 1));
                                state2 = stateUpdateChar(state2);
                                posStateNext = position + lenTest + 1 & encoder._posStateMask;
                                nextMatchPrice = curAndLenCharPrice + PROB_PRICES[(2048 - encoder._isMatch[(state2 << 4) + posStateNext]) >>> 2];
                                nextRepMatchPrice = nextMatchPrice + PROB_PRICES[(2048 - encoder._isRep[state2]) >>> 2];
                                offset = lenTest + 1 + lenTest2;
                                while (lenEnd < cur + offset) {
                                    encoder._optimum[lenEnd += 1].price = INFINITY_PRICE;
                                }
                                curAndLenPrice = nextRepMatchPrice + (price_3 = encoder._repMatchLenEncoder.getPrice(lenTest2 - 2, posStateNext), price_3 + this._GetPureRepPrice(0, state2, posStateNext));
                                optimum = encoder._optimum[cur + offset];
                                if (curAndLenPrice < optimum.price) {
                                    optimum.price = curAndLenPrice;
                                    optimum.posPrev = cur + lenTest + 1;
                                    optimum.backPrev = 0;
                                    optimum.prev1IsChar = 1;
                                    optimum.prev2 = 1;
                                    optimum.posPrev2 = cur;
                                    optimum.backPrev2 = curBack + 4;
                                }
                            }
                        }
                        offs += 2;
                        if (offs == numDistancePairs) {
                            break;
                        }
                    }
                }
            }
        }
        // Fallback return - should not be reached in normal execution
        return 1;
    }
    _LZMA_Encoder_GetPosLenPrice(pos, len, posState) {
        const encoder = this._compressor.chunker.encoder;
        let price, lenToPosState = getLenToPosState(len);
        if (pos < 128) {
            price = encoder._distancesPrices[lenToPosState * 128 + pos];
        }
        else {
            const position = (lenToPosState << 6) + this.GetPosSlot2(pos);
            price = encoder._posSlotPrices[position] + encoder._alignPrices[pos & 15];
        }
        return price + encoder._lenEncoder.getPrice(len - 2, posState);
    }
    _GetPureRepPrice(repIndex, state, posState) {
        const encoder = this._compressor.chunker.encoder;
        let price;
        if (!repIndex) {
            price = PROB_PRICES[(encoder._isRepG0[state]) >>> 2];
            price += PROB_PRICES[0x800 - this._compressor.chunker.encoder._isRep0Long[(state << 4) + posState] >>> 2];
        }
        else {
            price = PROB_PRICES[(0x800 - this._compressor.chunker.encoder._isRepG0[state]) >>> 2];
            if (repIndex == 1) {
                price += PROB_PRICES[(this._compressor.chunker.encoder._isRepG1[state]) >>> 2];
            }
            else {
                price += PROB_PRICES[(0x800 - this._compressor.chunker.encoder._isRepG1[state]) >>> 2];
                price += getBitPrice(this._compressor.chunker.encoder._isRepG2[state], repIndex - 2);
            }
        }
        return price;
    }
    _GetRepLen1Price(posState) {
        const encoder = this._compressor.chunker.encoder;
        const repG0Price = PROB_PRICES[(encoder._isRepG0[encoder._state]) >>> 2];
        const rep0LongPrice = PROB_PRICES[encoder._isRep0Long[(encoder._state << 4) + posState] >>> 2];
        return repG0Price + rep0LongPrice;
    }
    _MovePos(num) {
        if (num > 0) {
            this._Skip(num);
            this._compressor.chunker.encoder._additionalOffset += num;
        }
    }
    _ReadMatchDistances() {
        let lenRes = 0;
        const encoder = this._compressor.chunker.encoder;
        encoder._numDistancePairs = this._GetMatches();
        if (encoder._numDistancePairs > 0) {
            lenRes = encoder._matchDistances[encoder._numDistancePairs - 2];
            if (lenRes == encoder._numFastBytes) {
                lenRes += this._lzInWindow.getMatchLen(lenRes - 1, encoder._matchDistances[encoder._numDistancePairs - 1], 0x0111 - lenRes);
            }
        }
        encoder._additionalOffset += 1;
        return lenRes;
    }
    _ReleaseMFStream() {
        const encoder = this._compressor.chunker.encoder;
        if (encoder._matchFinder && encoder._needReleaseMFStream) {
            encoder._matchFinder._stream = null;
            encoder._needReleaseMFStream = 0;
        }
    }
    _ReleaseStreams() {
        this._ReleaseMFStream();
        this._compressor.chunker.encoder._rangeEncoder.stream = null;
    }
    writeHeaderProperties() {
        const HEADER_SIZE = 0x5; // Total header size in bytes
        // First byte combines posStateBits, literalPosStateBits and literalContextBits
        // Format: (posStateBits * 5 + literalPosStateBits) * 9 + literalContextBits
        this._encoder.properties[0] = ((this._encoder._posStateBits * 5 + this._encoder._numLiteralPosStateBits) * 9 + this._encoder._numLiteralContextBits) & 0xFF; // Ensure byte-sized value
        // Next 4 bytes store dictionary size in little-endian format
        for (let byteIndex = 0; byteIndex < 4; byteIndex++) {
            // Shift dictionary size right by appropriate number of bits and mask to byte
            this._encoder.properties[1 + byteIndex] = (this._encoder._dictionarySize >> (0x08 * byteIndex)) & 0xFF;
        }
        this._write_0(this._compressor.output, this._encoder.properties, 0, HEADER_SIZE);
    }
    GetPosSlot2(pos) {
        if (pos < 0x20000) {
            return G_FAST_POS[pos >> 6] + 12;
        }
        if (pos < 0x8000000) {
            return G_FAST_POS[pos >> 16] + 32;
        }
        return G_FAST_POS[pos >> 26] + 52;
    }
    _LZMA_Encoder_GetSubCoder(pos, prevByte) {
        const subCoder = this._compressor.chunker.encoder._literalEncoder.getSubCoder(pos, prevByte);
        return { decoders: subCoder.decoders };
    }
    _RangeCoder_Encoder_GetPrice_0(encoder, matchMode, matchByte, symbol) {
        let bit, context = 1, i = 7, matchBit, price = 0;
        if (matchMode) {
            for (; i >= 0; --i) {
                matchBit = (matchByte >> i) & 1;
                bit = (symbol >> i) & 1;
                price += getBitPrice(encoder.decoders[((1 + matchBit) << 8) + context], bit);
                context = context << 1 | bit;
                if (matchBit != bit) {
                    --i;
                    break;
                }
            }
        }
        for (; i >= 0; --i) {
            bit = symbol >> i & 1;
            price += getBitPrice(encoder.decoders[context], bit);
            context = context << 1 | bit;
        }
        return price;
    }
    _MakeAsChar(optimum) {
        optimum.backPrev = -1;
        optimum.prev1IsChar = 0;
    }
    _MakeAsShortRep(optimum) {
        optimum.backPrev = 0;
        optimum.prev1IsChar = 0;
    }
    ReverseEncode(startIndex, NumBitLevels, symbol) {
        let bit, m = 1;
        for (let i = 0; i < NumBitLevels; ++i) {
            bit = symbol & 1;
            this._compressor.chunker.encoder.encodeBit(this._compressor.chunker.encoder._posEncoders, startIndex + m, bit);
            m = m << 1 | bit;
            symbol >>= 1;
        }
    }
    _ReverseGetPrice(encoder, symbol) {
        let bit, m = 1, price = 0;
        for (let i = encoder.numBitLevels; i != 0; i -= 1) {
            bit = symbol & 1;
            symbol >>>= 1;
            price += getBitPrice(encoder.models[m], bit);
            m = m << 1 | bit;
        }
        return price;
    }
    _GetProcessedSizeAdd() {
        const processedCacheSize = add64(fromInt64(this._compressor.chunker.encoder._rangeEncoder.cacheSize), this._compressor.chunker.encoder._rangeEncoder.position);
        return add64(processedCacheSize, [4, 0]);
    }
    _decodeString(utf) {
        let j = 0, x, y, z, l = utf.length, buf = [], charCodes = [];
        for (let i = 0; i < l; ++i, ++j) {
            x = utf[i] & 0xFF;
            if (!(x & 0x80)) {
                if (!x) {
                    // It appears that this is binary data, so it cannot be
                    // converted to a string, so just send it back.
                    return utf;
                }
                charCodes[j] = x;
            }
            else if ((x & 0xE0) == 0xC0) {
                if (i + 1 >= l) {
                    // It appears that this is binary data, so it cannot be
                    // converted to a string, so just send it back.
                    return String.fromCharCode(...utf);
                }
                y = utf[++i] & 0xFF;
                if ((y & 0xC0) != 0x80) {
                    // It appears that this is binary data, so it cannot be
                    // converted to a string, so just send it back.
                    return String.fromCharCode(...utf);
                }
                charCodes[j] = ((x & 0x1F) << 6) | (y & 0x3F);
            }
            else if ((x & 0xF0) == 0xE0) {
                if (i + 2 >= l) {
                    // It appears that this is binary data, so it cannot be
                    // converted to a string, so just send it back.
                    return utf;
                }
                y = utf[++i] & 0xFF;
                if ((y & 0xC0) != 0x80) {
                    // It appears that this is binary data, so it cannot be converted to
                    // a string, so just send it back.
                    return utf;
                }
                z = utf[++i] & 0xFF;
                if ((z & 0xC0) != 0x80) {
                    // It appears that this is binary data, so it cannot be converted to
                    // a string, so just send it back.
                    return utf;
                }
                charCodes[j] = ((x & 0x0F) << 0x0C) | ((y & 0x3F) << 6) | (z & 0x3F);
            }
            else {
                // It appears that this is binary data, so it cannot be converted to
                // a string, so just send it back.
                return utf;
            }
            if (j == 0x3FFF) {
                buf.push(String.fromCharCode.apply(String, charCodes));
                j = -1;
            }
        }
        if (j > 0) {
            charCodes.length = j;
            buf.push(String.fromCharCode.apply(String, charCodes));
        }
        return buf.join("");
    }
    encodeString(inputString) {
        let ch, chars = [], elen = 0, l = inputString.length;
        this._getChars(inputString, 0, l, chars, 0);
        // Add extra spaces in the array to break up the unicode symbols.
        for (let i = 0; i < l; ++i) {
            ch = chars[i];
            if (ch >= 1 && ch <= 127) {
                ++elen;
            }
            else if (!ch || ch >= 128 && ch <= 2047) {
                elen += 2;
            }
            else {
                elen += 3;
            }
        }
        const data = [];
        elen = 0;
        for (let i = 0; i < l; ++i) {
            ch = chars[i];
            if (ch >= 1 && ch <= 0x7F) { // 127
                data[elen++] = ch << 24 >> 24;
            }
            else if (!ch || ch >= 0x80 && ch <= 0x7FF) {
                data[elen++] = (0xC0 | ch >> 6 & 0x1F) << 24 >> 24;
                data[elen++] = (0x80 | ch & 0x3F) << 24 >> 24;
            }
            else {
                data[elen++] = (0xE0 | ch >> 12 & 0x0F) << 24 >> 24;
                data[elen++] = (0x80 | ch >> 6 & 0x3F) << 24 >> 24;
                data[elen++] = (0x80 | ch & 0x3F) << 24 >> 24;
            }
        }
        return data;
    }
    compress(data, mode = 5) {
        const compressionMode = MODES[mode];
        this._byteArrayCompressor(data, compressionMode);
        while (this._compressor.chunker.processChunk())
            ;
        const result = this._toByteArray(this._compressor.output);
        return new Int8Array(result);
    }
    compressString(data, mode = 5) {
        const encodedData = this.encodeString(data);
        return this.compress(new Uint8Array(encodedData), mode);
    }
    decompress(bytearray) {
        this._byteArrayDecompressor(bytearray);
        while (this._decompressor.chunker.processChunk())
            ;
        return this._toByteArray(this._decompressor.output);
    }
    decompressString(bytearray) {
        this._byteArrayDecompressor(bytearray);
        while (this._decompressor.chunker.processChunk())
            ;
        const decodedByteArray = this._toByteArray(this._decompressor.output);
        const result = this._decodeString(decodedByteArray);
        if (typeof result === "string") {
            return result;
        }
        else {
            // If decoding failed and returned binary data, convert to string anyway
            return String.fromCharCode(...result);
        }
    }
    // Public methods for chunker access
    codeOneBlock() {
        this._CodeOneBlock();
    }
    releaseStreams() {
        this._ReleaseStreams();
    }
}
